// Token context + template rendering shared by BOTH Tank caption renderers:
// the quick-action path (`resolveTemplate` in CaptionBuilder.jsx) and the
// Caption Builder chain path (`buildCaption` below). Until 06-09-2026 the
// chain path had its own three-token replace ({score}, {team}, {team_abbr})
// and printed every other token literally, so every chain caption authored
// since migration 0087 (22-08) shipped "leads {pronoun_possessive} team out".
//
// This is the THIRD renderer of these tokens, after
// iptc_templates.build_template_context (server) and matrix-app's
// captionTokens.ts. All three must agree: Tank freezes its render into
// photo.caption_override, and that string beats every template on the way
// out. Unknown tokens resolve to '' on every renderer.

export function getTeamAbbr(teamName, prefixes, shortName) {
  if (shortName) return shortName.toUpperCase();
  if (!teamName) return '';
  let name = teamName.trim();
  for (const prefix of prefixes) {
    const re = new RegExp(`^${prefix}\\s+`, 'i');
    if (re.test(name)) {
      name = name.replace(re, '').trim();
      break;
    }
  }
  return name.slice(0, 3).toUpperCase();
}

export function formatScore(home, away) {
  return `${home}-${away}`;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const GOAL_ORDINALS = [
  '', 'first', 'second', 'third', 'fourth', 'fifth',
  'sixth', 'seventh', 'eighth', 'ninth', 'tenth',
];

// Mirrors backend iptc_templates._join_with_and.
function joinAnd(parts) {
  const arr = parts.filter(Boolean);
  if (arr.length === 0) return '';
  if (arr.length === 1) return arr[0];
  return `${arr.slice(0, -1).join(', ')} and ${arr[arr.length - 1]}`;
}

// {goal_ordinal} -- Getty's scoring line:
//   "celebrating scoring his team's first goal (1-1)"
//
// The ordinal is the scoring side's own tally, i.e. their half of the score.
// `subject` is either a bare team name (chain path: the node's person's team)
// or the "<Name> of <Team>" label (quick-action path: primary_subject_team).
// Side decided by computeSubjectSide below (shared with {score_state}). Empty
// wherever it would be a guess -- no score, subject on neither side, a tally
// of zero, or past the cleared vocabulary. The whitespace collapse every
// renderer applies then turns "team's  goal" into "team's goal", which still
// reads.
export function computeGoalOrdinal(subjectTeam, { matchData, homeScore, awayScore }) {
  const side = computeSubjectSide(subjectTeam, matchData);
  if (!side) return '';
  const tally = side === 'home' ? homeScore : awayScore;
  return tally >= 1 && tally < GOAL_ORDINALS.length ? GOAL_ORDINALS[tally] : '';
}

// Which fixture side the subject is on: 'home' | 'away' | '' (unknowable).
// Mirrors `_subject_side` in iptc_templates.py and matrix-app's
// `resolveSubjectSide`, down to the string matching. Shared by {goal_ordinal}
// and {score_state} so the two tokens in one phrase never disagree about the
// person's team.
export function computeSubjectSide(subjectTeam, matchData) {
  const subject = (subjectTeam || '').trim();
  if (!subject) return '';
  const home = matchData?.home_team_name || '';
  const away = matchData?.away_team_name || '';
  const idx = subject.lastIndexOf(' of ');
  const teamPart = idx >= 0 ? subject.slice(idx + 4) : subject;
  // Pass 0: pool-root equality on the team part (identity, not heuristics).
  // Render-never fields on the match payload; '' on payloads that predate
  // them. Both roots matching is a mis-linked fixture, so refuse.
  const homePoolRoot = (matchData && matchData.home_pool_root) || '';
  const awayPoolRoot = (matchData && matchData.away_pool_root) || '';
  const rootHits = [[homePoolRoot, 'home'], [awayPoolRoot, 'away']]
    .filter(([root]) => root && teamPart === root);
  if (rootHits.length === 1) return rootHits[0][1];
  if (rootHits.length > 0) return '';
  // Pass 1: exact suffix, longest name first.
  const sides = [[home, 'home'], [away, 'away']]
    .sort((a, b) => String(b[0]).length - String(a[0]).length);
  for (const [name, side] of sides) {
    if (name && subject.endsWith(name)) return side;
  }
  // Pass 2: the affiliation carries the CLUB while the fixture may field an
  // UNLINKED derived side ("Jong ...", "... Vrouwen"). Containment either
  // way around; both sides containing it is a guess.
  const hits = sides.filter(
    ([name]) => name && teamPart && (name.includes(teamPart) || teamPart.includes(name)),
  );
  return hits.length === 1 ? hits[0][1] : '';
}

// {score_state} -- the running score as a clause from the subject's side:
//   "his team leading 2-1" / "her team trailing 1-2" / "the sides level at 1-1"
//
// Mirrors `_score_state` in iptc_templates.py and matrix-app's buildContext.
// One clause rather than a bare word because the level form takes "at" and
// the other two take nothing, and a chip has ONE template. '' at 0-0 (held as
// "unset", like score_phrase and goal_ordinal) and wherever the side is a
// guess; the quick-action post-process then drops the trailing "with".
export function computeScoreState(subjectTeam, { matchData, homeScore, awayScore }) {
  if (typeof homeScore !== 'number' || typeof awayScore !== 'number') return '';
  if (homeScore === 0 && awayScore === 0) return '';
  const side = computeSubjectSide(subjectTeam, matchData);
  if (!side) return '';
  const pronoun = (matchData && matchData.subject_pronoun) || 'his';
  const [own, other] = side === 'home' ? [homeScore, awayScore] : [awayScore, homeScore];
  const score = formatScore(homeScore, awayScore);
  if (own > other) return `${pronoun} team leading ${score}`;
  if (own < other) return `${pronoun} team trailing ${score}`;
  return `the sides level at ${score}`;
}

// Build the full token map from match + photo context. Mirrors the backend's
// build_template_context so company caption_templates and Caption Builder
// templates render identically client-side.
//
// inputs:
//   matchData        -- GET /api/matches/<id>/ payload (or null)
//   photographer     -- photo's photographer object (or null)
//   homeScore/awayScore -- numeric score state
//   personTokenLists -- { primaryName, primaryTeam, othersNames, othersTeams } or null
//   matchPhase       -- 'before' | 'during' | 'after' | null
export function buildTokenContext({
  matchData,
  photographer,
  homeScore,
  awayScore,
  personTokenLists,
  matchPhase,
}) {
  const home = matchData?.home_team_name || '';
  const away = matchData?.away_team_name || '';
  const venue = matchData?.venue || '';
  const matchName =
    matchData?.name ||
    (home && away ? `${home} vs ${away}` : '') ||
    'match';
  const dateObj = matchData?.start_time ? new Date(matchData.start_time) : null;
  const date = dateObj ? dateObj.toLocaleDateString() : '';

  // City / country / season / competition / day / month / monthname / year,
  // sourced from matchData.iptc_config and the start_time.
  const iptc = matchData?.iptc_config || {};
  const city = iptc.city || matchData?.city || '';
  const state = iptc.state || '';
  const country = iptc.country || matchData?.country || '';
  const location = iptc.location || '';
  // Mirrors the backend's `match.season or derive_season(start_time)`. Tank
  // used to stop at the explicit override, so a match with no season typed
  // rendered an empty season here while the server derived one -- and because
  // this render is frozen into caption_override, the empty one is what
  // shipped. Football convention: August-July, Jan-Jul belongs to the season
  // that started the previous calendar year.
  const season = matchData?.season || (() => {
    const s = matchData?.start_time ? new Date(matchData.start_time) : null;
    if (!s || Number.isNaN(s.getTime())) return '';
    const startYear = s.getMonth() + 1 <= 7 ? s.getFullYear() - 1 : s.getFullYear();
    return `${startYear}/${startYear + 1}`;
  })();
  const competition = matchData?.competition_display || matchData?.competition || '';
  const homeTeam = matchData?.home_team_name || home;
  const awayTeam = matchData?.away_team_name || away;
  const event = matchData?.name
    || (homeTeam && awayTeam ? `${homeTeam} vs ${awayTeam}` : '');
  const team = homeTeam;
  const photog = (() => {
    // Mirrors build_template_context: export_byline wins, then the full name,
    // then the username. The byline is the credited string and may carry an
    // agency ("Rene Nijhuis/MB Media"), so resolving it from the name alone
    // renders a caption that disagrees with dc:Creator in the same file --
    // and this render is frozen into caption_override.
    const p = photographer || matchData?.photographer;
    if (!p) return '';
    const byline = (p.export_byline || '').trim();
    if (byline) return byline;
    const full = `${p.first_name || ''} ${p.last_name || ''}`.trim();
    return full || p.name || p.username || '';
  })();
  const day = dateObj ? String(dateObj.getDate()) : '';
  const day0 = dateObj ? String(dateObj.getDate()).padStart(2, '0') : '';
  const month = dateObj ? String(dateObj.getMonth() + 1).padStart(2, '0') : '';
  const monthname = dateObj ? MONTH_NAMES[dateObj.getMonth()] : '';
  const year4 = dateObj ? String(dateObj.getFullYear()) : '';

  // Score + safe phrase. Score state is held as numbers; treat 0-0 as
  // "unset" for the natural-language phrase.
  const hasRealScore = !(homeScore === 0 && awayScore === 0);
  const score = formatScore(homeScore, awayScore);
  const scorePhrase = hasRealScore ? `the ${homeScore}-${awayScore}` : 'a goal';

  // Person tokens.
  const t = personTokenLists;
  const primarySubject = t?.primaryName || '';
  const primarySubjectTeam = t?.primaryTeam || primarySubject;
  const others = t ? t.othersNames.join(', ') : '';
  const othersAnd = t ? joinAnd(t.othersNames) : '';
  const othersTeamAnd = t ? joinAnd(t.othersTeams) : '';
  const allNames = t ? [primarySubject, ...t.othersNames].filter(Boolean) : [];
  const allTeams = t ? [primarySubjectTeam, ...t.othersTeams].filter(Boolean) : [];
  const personsAnd = joinAnd(allNames);
  const personsTeamAnd = joinAnd(allTeams);
  const persons = t ? allNames.join(', ') : '';

  // The channel identity. Tank's context once carried NO `copyright` key at
  // all, so `({copyright}/{photog})` rendered "(/Danny de Groot)". Read from
  // `company_detail`: `company` is a bare UUID on the match payload. Mirrors
  // matrix-app's buildContext, including stripping the "(c) " prefix -- the
  // caption says "(Hero Images/...)", not "((c) Hero Images/...)".
  const companyDefaults = (matchData && matchData.company_detail) || {};
  const companyIptc = companyDefaults.iptc_defaults || {};
  const copyright = String(companyIptc.copyright || '')
    .replace('(c) ', '')
    .replace('(c)', '');
  const copyrighturl = String(companyIptc.copyright_url || '');
  const companyName = String(companyDefaults.name || '');

  const pronounPossessive =
    (matchData && matchData.subject_pronoun) || 'his';
  const goalOrdinal = computeGoalOrdinal(primarySubjectTeam, { matchData, homeScore, awayScore });
  const scoreState = computeScoreState(primarySubjectTeam, { matchData, homeScore, awayScore });

  return {
    home, away, venue, match_name: matchName, score, score_phrase: scorePhrase,
    pronoun_possessive: pronounPossessive, goal_ordinal: goalOrdinal,
    score_state: scoreState,
    copyright, copyrighturl, company: companyName,
    // {round} -- free-form label off the match, like the server's
    // build_template_context. USUALLY BLANK, which is exactly how a missing
    // key hides: while no round is set both sides render '' and agree.
    round: (matchData && matchData.round) || '',
    date, day, day0, month, monthname, year4, season, competition,
    city, state, country, location, event, team, photog,
    home_team: homeTeam, away_team: awayTeam,
    // What `{home_team:common}` & co. render: the server's own value (match
    // payload, 26-09-2026), else the team's name -- the same fallback.
    home_team_common: (matchData && matchData.home_team_common) || homeTeam,
    away_team_common: (matchData && matchData.away_team_common) || awayTeam,
    team_common: (matchData && matchData.home_team_common) || homeTeam,
    // {match_phase} mirrors the backend default: "during" when we can't
    // derive a phase from capture_date + match times.
    match_phase: matchPhase || 'during',
    primary_subject: primarySubject,
    primary_subject_team: primarySubjectTeam,
    others, others_and: othersAnd, others_team_and: othersTeamAnd,
    persons, persons_and: personsAnd, persons_team_and: personsTeamAnd,
  };
}

// Mirrors iptc_templates._shorten_season: 2026/2027 -> 2026/27. Must stay in
// step with the backend, because Tank BAKES its render into
// photo.caption_override. A divergence here is not a preview bug, it is a
// delivered one.
function applyMod(value, modifier) {
  if (!modifier) return value ?? '';
  const v = value == null ? '' : String(value);
  if (modifier === 'short') {
    const m = /^(\d{4})\/(\d{4})$/.exec(v.trim());
    return m ? `${m[1]}/${m[2].slice(2)}` : v;
  }
  if (modifier === 'upper') return v.toUpperCase();
  if (modifier === 'lower') return v.toLowerCase();
  if (modifier === 'title') return v.replace(/\w\S*/g, (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase());
  return v;
}

const ARTICLE_BEFORE = /(?:^|[^A-Za-z])the\s+$/i;

// A team name as it reads mid-sentence, for `:sentence`. MIRRORS the server's
// `sentence_form` and the matrix-app's `sentenceForm`; all three are tested
// against src/contracts/team_sentence_cases.json. Only a name starting with
// exactly "The " changes: to "the ", or, after a literal "the" in the template
// (`before`), to the name without its article.
export function sentenceForm(value, before = '') {
  const v = value == null ? '' : String(value);
  if (!v.startsWith('The ')) return v;
  if (ARTICLE_BEFORE.test(before)) return v.slice(4);
  return `the ${v.slice(4)}`;
}

// First character upper case, the rest untouched (server `capitalise_first`).
export function capitaliseFirst(text) {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

// `{name:sentence}`: the name with its role prefix as stored, the mid-sentence
// form ("head coach Robin van Persie"). `name` carries the prefix capitalised;
// a name without it is returned as is. MIRRORS the server's `sentence_name`.
export function sentenceName(record) {
  const name = record.name ?? '';
  const role = String(record.prefix ?? '').trim();
  if (role && name.startsWith(`${capitaliseFirst(role)} `)) return role + name.slice(role.length);
  return name;
}

// Substitute {var} or {var:modifier} against ctx (+ overrides, which win).
// Unknown tokens render as ''. No sentence-level post-processing: chain
// templates legitimately end in "with" / "from" for the next person node.
export function substituteTokens(template, ctx, overrides = null) {
  if (typeof template !== 'string') return '';
  const map = overrides ? { ...ctx, ...overrides } : ctx;
  return template.replace(/\{(\w+)(?::(\w+))?\}/g, (_match, name, modifier, offset) => {
    const val = Object.prototype.hasOwnProperty.call(map, name) ? map[name] : '';
    // `common` picks a different VALUE (server resolve_token): `<name>_common`,
    // else the name itself.
    if (modifier === 'common') return map[`${name}_common`] || (val == null ? '' : String(val));
    // `sentence` reads the template text before the token (server resolve_token).
    if (modifier === 'sentence') return sentenceForm(val, template.slice(0, offset));
    // The persons phrase starts the caption sentence: a capital (server resolve_token).
    if (name === 'persons') return applyMod(capitaliseFirst(val == null ? '' : String(val)), modifier);
    return applyMod(val, modifier);
  });
}

// Everything a per-person template may name (server PERSON_TEMPLATE_KEYS).
export const PERSON_TEMPLATE_KEYS = ['name', 'team', 'display', 'bare', 'first', 'last', 'prefix', 'suffix', 'jersey'];

// Render ONE person with a per-person template. MIRRORS the server's
// `_render_person` and the matrix-app's `renderPerson`; all three are tested
// against src/contracts/person_phrase_cases.json. `{team:common}` is
// `team_common`, falling back to `team`; an empty value collapses the phrase
// around it (no dangling "of", no "()").
export function renderPerson(template, record) {
  const source = String(template);
  // First, on the template as written: `{team:sentence}` reads the text before it.
  let text = source.replace(/\{team:sentence\}/g, (_m, offset) =>
    sentenceForm(record.team, source.slice(0, offset)),
  );
  text = text.split('{name:sentence}').join(sentenceName(record));
  text = text.split('{team:common}').join(record.team_common || record.team || '');
  for (const key of PERSON_TEMPLATE_KEYS) {
    text = text.split(`{${key}}`).join(record[key] ?? '');
  }
  text = text.replace(/\(\s*\)|\[\s*\]/g, '');
  // "of the {team:sentence}" with no team leaves "of the": both go.
  text = text.trim().replace(/\s+(of|van|voor)(\s+the)?\s*$/i, '');
  text = text.replace(/\s{2,}/g, ' ');
  return text.replace(/^[ ,\-|]+|[ ,\-|]+$/g, '');
}

// How the editor's phrase names one person: the SERVER's team label for this
// match (person_team_labels) rendered with the company's house person rule
// (photo.editor_persons_template, 26-09-2026). No rule (older server) = the
// "<name> of <team>" this code always wrote.
export function personPhraseLabel(displayName, person, label, template) {
  const team = label?.team_name || '';
  if (!template) return team ? `${displayName} of ${team}` : displayName;
  const plain = `${person?.first_name || ''} ${person?.last_name || ''}`.trim() || displayName;
  return renderPerson(template, {
    name: displayName,
    team,
    team_common: label?.team_common || '',
    display: plain,
    bare: plain,
    first: person?.first_name || '',
    last: person?.last_name || '',
    prefix: person?.prefix || '',
    suffix: person?.suffix || '',
  });
}

// Full quick-action render: substitution plus the sentence post-process.
// Drops dangling " with .", collapses double spaces, trims spaces before
// punctuation. Keeps the caption readable when optional tokens (e.g.
// {others_and}) resolve to empty.
export function renderTemplate(template, ctx, overrides = null) {
  if (typeof template !== 'string') return '';
  let out = substituteTokens(template, ctx, overrides);
  out = out.replace(/\s+with\s*([.,;:!?])/g, '$1');
  out = out.replace(/\s+with\s*$/g, '');
  out = out.replace(/\s{2,}/g, ' ');
  out = out.replace(/\s+([.,;:!?])/g, '$1');
  return out.trim();
}

// Caption Builder chain renderer. Each person node renders as
// "<Name> of <Team> <action template>", connectors verbatim. The action
// template goes through the SAME token context as the quick actions, with
// three node-scoped overrides:
//   {team} / {team_abbr} -- the node's person's team, not the match home side
//   {goal_ordinal}       -- that team's tally: the node's person is the
//                           sentence subject, not the photo's primary subject
//   {score_state}        -- same subject rule: leading / trailing / level
//                           from the node person's side
//   {score}              -- only when the action asks for it (needsScore),
//                           as the chain path always did
// Angle-bracket optional markers (<text>) are unwrapped after substitution.
export function buildCaption(chain, tokenInputs, teamPrefixes) {
  const ctx = buildTokenContext(tokenInputs);
  return chain
    .map((node) => {
      if (node.type === 'person') {
        const teamName = node.person.team_memberships?.[0]?.team_name;
        const teamShortName = node.person.team_memberships?.[0]?.team_short_name;
        // The label Edit.jsx built for this person (server team label + the
        // house person rule) when there is one; the old first-membership
        // phrase only when the chain runs without it.
        const fullName =
          tokenInputs?.personLabels?.[String(node.person.id)] ||
          (teamName ? `${node.person.display_name} of ${teamName}` : node.person.display_name);
        if (node.action) {
          const overrides = {
            score: node.action.needsScore ? ctx.score : '',
            team: teamName || '',
            team_abbr: getTeamAbbr(teamName, teamPrefixes || [], teamShortName),
            goal_ordinal: teamName
              ? computeGoalOrdinal(teamName, tokenInputs)
              : ctx.goal_ordinal,
            score_state: teamName
              ? computeScoreState(teamName, tokenInputs)
              : ctx.score_state,
          };
          let tmpl = substituteTokens(node.action.template, ctx, overrides);
          // Strip angle-bracket optional markers: <text> becomes text
          tmpl = tmpl.replace(/<([^>]+)>/g, (_, inner) => inner);
          return `${fullName} ${tmpl}`;
        }
        return fullName;
      }
      return node.text || '';
    })
    .join(' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}
